/**
 * Inbox index child (`/approvals`) — shown when no specific approval is
 * selected. Auto-navigates to the first pending item so the reading pane
 * is never blank when there are items to review.
 *
 * The WorkspaceShell + InboxLayout live in the parent layout route
 * `approvals.tsx`; this component only supplies the right-pane content.
 */
import { useEffect } from "react";
import { createFileRoute, useNavigate, useSearch } from "@tanstack/react-router";
import { trpc } from "../../../lib/trpc.ts";
import { type InboxItem, type InboxStatus } from "../../../features/inbox/index.ts";
import { ReadingPane } from "../../../features/inbox/reading-pane.tsx";

export const Route = createFileRoute("/(workspace)/approvals/")({
  component: InboxIndexRoute,
});

function InboxIndexRoute() {
  const navigate = useNavigate();
  // Inherit the parent layout route's validated search params.
  const parentSearch = useSearch({ from: "/(workspace)/approvals" });
  const status: InboxStatus = parentSearch.status ?? "pending";

  // Hit the parent layout's already-cached query — no extra network call.
  const list = trpc.approvals.list.useQuery({ limit: 50, status });
  const items = (list.data?.items ?? []) as InboxItem[];

  // Auto-select the first item once the list loads.
  useEffect(() => {
    if (items.length > 0) {
      void navigate({
        to: "/approvals/$approvalId",
        params: { approvalId: items[0]!.id },
        search: status !== "pending" ? { status } : {},
        replace: true,
      });
    }
  }, [items, navigate, status]);

  // Shown briefly while loading, or permanently when the inbox is empty.
  return <ReadingPane item={null} isFounder={false} sellerName="" onDecide={() => {}} decidePending={false} />;
}
